import DashboardLayout from "../dashboard/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { useOrganization } from "../../App";
import { supabase } from "../../../supabase/supabase";
import { LoadingSpinner } from "../ui/loading-spinner";
import { Clock, Phone, Users, Bot, FileText } from "lucide-react";

interface AnalyticsStats {
  totalAgents: number;
  totalMinutesSpoken: number;
  totalLeads: number;
  totalCallRequests: number;
  totalCallLogs: number;
}

export default function AnalyticsPage() {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    if (currentOrganization) {
      fetchStats();
    }
  }, [currentOrganization]);

  const fetchStats = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      // Fetch total agents (assistants)
      const { data: agents, error: agentsError } = await supabase
        .from("assistants")
        .select("id")
        .eq("organization_id", currentOrganization.id);

      // Fetch total call minutes
      const { data: callLogs, error: callLogsError } = await supabase
        .from("call_logs")
        .select("call_duration")
        .eq("organization_id", currentOrganization.id);

      // Fetch total leads
      const { data: leads, error: leadsError } = await supabase
        .from("leads")
        .select("id")
        .eq("organization_id", currentOrganization.id);

      // Fetch total call requests
      const { data: callRequests, error: callRequestsError } = await supabase
        .from("call_requests")
        .select("id")
        .eq("organization_id", currentOrganization.id);

      if (agentsError || callLogsError || leadsError || callRequestsError) {
        console.error("Error fetching stats:", {
          agentsError,
          callLogsError,
          leadsError,
          callRequestsError,
        });
        return;
      }

      // Calculate total minutes from call durations
      const totalMinutes = callLogs
        ? callLogs.reduce((acc, log) => acc + (log.call_duration || 0), 0) / 60
        : 0;

      setStats({
        totalAgents: agents?.length || 0,
        totalMinutesSpoken: Math.round(totalMinutes),
        totalLeads: leads?.length || 0,
        totalCallRequests: callRequests?.length || 0,
        totalCallLogs: callLogs?.length || 0,
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    description,
    index = 0,
  }: {
    title: string;
    value: number;
    icon: any;
    description: string;
    index?: number;
  }) => {
    // Define color palettes for the cards
    const colors = [
      { bg: "bg-secondary/20", border: "border-border-border", text: "text-foreground dark:text-ink-faint", icon: "text-foreground" },
      { bg: "bg-good-bg-good-bg/20", border: "border-good-line", text: "text-foreground dark:text-ink-faint", icon: "text-good" },
      { bg: "bg-secondary/20", border: "border-border-border", text: "text-foreground dark:text-ink-faint", icon: "text-foreground" },
      { bg: "bg-mid-bg-mid-bg/20", border: "border-mid-line", text: "text-foreground dark:text-ink-faint", icon: "text-mid" },
      { bg: "bg-danger-bg-danger-bg/20", border: "border-danger-line", text: "text-foreground dark:text-ink-faint", icon: "text-danger" },
    ];
    const colorSet = colors[index % colors.length];

    return (
      <div
        className={`relative overflow-hidden ${colorSet.bg} p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border ${colorSet.border} group`}
      >
        <div className="flex flex-row items-center justify-between pb-2 space-y-0">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center bg-card/60 dark:bg-black/20 backdrop-blur-sm transform transition-all duration-300 group-hover:scale-110 ${colorSet.icon}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className={`text-2xl font-bold mt-1 ${colorSet.text} transition-all duration-300 group-hover:translate-x-1 group-hover:scale-105`}>
          {value.toLocaleString()}
        </div>
        <p className="text-xs text-muted-foreground mt-1 transition-all duration-300 group-hover:opacity-90">
          {description}
        </p>
      </div>
    );
  };

  return (
    <DashboardLayout
      activeItem="Analytics"
      title="Analytics Dashboard"
      description="Overview of your organization's activity"
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-[400px]">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <StatCard
            title="Total Agents"
            value={stats?.totalAgents || 0}
            icon={Bot}
            description="Active AI assistants"
            index={0}
          />
          <StatCard
            title="Minutes Spoken"
            value={stats?.totalMinutesSpoken || 0}
            icon={Clock}
            description="Total conversation time"
            index={1}
          />
          <StatCard
            title="Total Leads"
            value={stats?.totalLeads || 0}
            icon={Users}
            description="Leads in database"
            index={2}
          />
          <StatCard
            title="Call Requests"
            value={stats?.totalCallRequests || 0}
            icon={Phone}
            description="Total call requests"
            index={3}
          />
          <StatCard
            title="Call Logs"
            value={stats?.totalCallLogs || 0}
            icon={FileText}
            description="Completed calls"
            index={4}
          />
        </div>
      )}
    </DashboardLayout>
  );
}
